import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import AuthUtility from "../../util/AuthUtility";
import {
  Col, Button, Row, Container, Card, Form,
  InputGroup, Table, Pagination, Badge,
} from "react-bootstrap";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import { Page } from "../../dto/page/Page";
import { DeliveryZoneEntity } from "../../dto/deliveryzone/DeliveryZoneEntity";
import { DeliveryZoneSearch } from "../../dto/deliveryzone/DeliveryZoneSearch";
import { onDeliveryZoneSearch } from "../../controller/DeliveryZoneController";
import { splitDate } from "../../app/Utils";
import { DeliveryZoneEditDrawer } from "../../component/DeliveryZoneEditDrawer";

interface ZoneListState {
  data: DeliveryZoneEntity[];
  page?: Page;
}

const getStatusBadge = (status?: string) => {
  if (status === "ACTIVE") return "success";
  if (status === "INACTIVE") return "secondary";
  return "light";
};

const DeliveryZoneList = () => {
  const navigate = useNavigate();

  useEffect(() => {
    if (!AuthUtility.isUserLoggedIn()) {
      navigate("/login");
      window.location.reload();
    }
  }, []);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedZone, setSelectedZone] = useState<DeliveryZoneEntity | null>(null);

  const openCreate = () => {
    setSelectedZone(null);
    setDrawerOpen(true);
  };

  const openEdit = (zone: DeliveryZoneEntity) => {
    setSelectedZone(zone);
    setDrawerOpen(true);
  };

  const [search, setSearch] = useState<DeliveryZoneSearch>({ limit: 10 });
  const [zones, setZones] = useState<ZoneListState>({ data: [] });

  const searchSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    load(0);
  };

  const changePage = (page: number) => {
    if (!zones.page) return;
    if (page < 0 || page >= zones.page.totalPages) return;
    load(page);
  };

  const load = async (page: number = 0) => {
    const params: DeliveryZoneSearch = { ...search, offset: page };
    try {
      const response = await onDeliveryZoneSearch(params);
      if (response.status !== 0) {
        toast.error("Хүргэлтийн бүс хайхад алдаа гарлаа");
        return;
      }
      const pageable = response.data;
      const pageInfo: Page = {
        limit: search.limit,
        totalElements: pageable.totalElements,
        totalPages: pageable.totalPages,
        offset: page,
      };
      setZones({ page: pageInfo, data: pageable.beans });
      setSearch((prev) => ({ ...prev, offset: page }));
    } catch {
      toast.error("Сүлжээний алдаа гарлаа");
    }
  };

  return (
    <Container fluid>
      <div><Toaster position="top-right" reverseOrder={false} /></div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3>
          <a onClick={() => navigate(-1)} style={{ cursor: "pointer" }}><big>&#8592;</big></a>
          {" "}Хүргэлтийн бүс
        </h3>
        <Button variant="primary" size="sm" onClick={openCreate}>
          <AddIcon fontSize="small" /> Бүс үүсгэх
        </Button>
      </div>

      <Row className="d-flex justify-content-center align-items-center mb-3">
        <Col md={12}>
          <Card>
            <Card.Header>
              <Form onSubmit={searchSubmit}>
                <Row>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Нэр</Form.Label>
                      <Form.Control
                        size="sm"
                        type="text"
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value !== "") {
                            setSearch({ ...search, name: value });
                          } else {
                            const { name, ...rest } = search;
                            setSearch(rest);
                          }
                        }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Статус</Form.Label>
                      <Form.Select
                        size="sm"
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value !== "") {
                            setSearch({ ...search, status: value });
                          } else {
                            const { status, ...rest } = search;
                            setSearch(rest);
                          }
                        }}
                      >
                        <option value=""></option>
                        <option value="ACTIVE">ACTIVE</option>
                        <option value="INACTIVE">INACTIVE</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Багцлах</Form.Label>
                      <InputGroup>
                        <Form.Select
                          size="sm"
                          onChange={(e) =>
                            setSearch({ ...search, limit: parseInt(e.target.value) })
                          }
                        >
                          <option value="10">10</option>
                          <option value="25">25</option>
                          <option value="0">Бүгд</option>
                        </Form.Select>
                        <Button variant="primary" size="sm" type="submit">
                          <SearchIcon fontSize="small" />
                        </Button>
                      </InputGroup>
                    </Form.Group>
                  </Col>
                </Row>
              </Form>
            </Card.Header>

            <Card.Body>
              <div className="table-responsive">
                <Table hover>
                  <thead>
                    <tr>
                      <th>Нэр</th>
                      <th>Төв (Lat, Lng)</th>
                      <th>Радиус (м)</th>
                      <th>Төлбөр</th>
                      <th>Валют</th>
                      <th>ETA</th>
                      <th>Эрэмбэ</th>
                      <th>Статус</th>
                      <th>Үүсгэсэн</th>
                      <th>Шинэчилсэн</th>
                    </tr>
                  </thead>
                  <tbody>
                    {zones.data.length === 0 && (
                      <tr>
                        <td colSpan={11} className="text-muted">Илэрц олдсонгүй</td>
                      </tr>
                    )}
                    {zones.data.map((zone, idx) => (
                      <tr key={idx}>
                        <td
                          style={{ cursor: "pointer", color: "#1976d2", fontWeight: 500 }}
                          onClick={() => openEdit(zone)}
                        >
                          {zone.name}
                        </td>
                        <td style={{ fontSize: 12, color: "#555" }}>
                          {zone.centerLat?.toFixed(5)}, {zone.centerLng?.toFixed(5)}
                        </td>
                        <td>{zone.maxRadiusM?.toLocaleString()}</td>
                        <td>{zone.feeAmount?.toLocaleString()}</td>
                        <td>{zone.currency}</td>
                        <td>{zone.etaText}</td>
                        <td>{zone.priority}</td>
                        <td>
                          <Badge
                            bg={getStatusBadge(zone.status)}
                            className={getStatusBadge(zone.status) === "light" ? "text-dark" : ""}
                          >
                            {zone.status}
                          </Badge>
                        </td>
                        <td>
                          {splitDate(zone.createdAt as any)?.date}{" "}
                          {splitDate(zone.createdAt as any)?.time}
                        </td>
                        <td>
                          {splitDate(zone.updatedAt as any)?.date}{" "}
                          {splitDate(zone.updatedAt as any)?.time}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        {zones.page && zones.page.totalPages > 1 && (() => {
          const page = zones.page;
          return (
            <Row>
              <Col className="d-flex justify-content-start mt-3">
                <Pagination>
                  <Pagination.First disabled={page.offset === 0} onClick={() => changePage(0)} />
                  <Pagination.Prev disabled={page.offset === 0} onClick={() => changePage(page.offset - 1)} />
                  {Array.from({ length: page.totalPages })
                    .slice(Math.max(page.offset - 2, 0), Math.min(page.offset + 3, page.totalPages))
                    .map((_, idx) => {
                      const pageNumber = Math.max(page.offset - 2, 0) + idx;
                      return (
                        <Pagination.Item
                          key={pageNumber}
                          active={pageNumber === page.offset}
                          onClick={() => changePage(pageNumber)}
                        >
                          {pageNumber + 1}
                        </Pagination.Item>
                      );
                    })}
                  <Pagination.Next
                    disabled={page.offset + 1 >= page.totalPages}
                    onClick={() => changePage(page.offset + 1)}
                  />
                  <Pagination.Last
                    disabled={page.offset + 1 >= page.totalPages}
                    onClick={() => changePage(page.totalPages - 1)}
                  />
                </Pagination>
              </Col>
            </Row>
          );
        })()}
      </Row>

      <DeliveryZoneEditDrawer
        open={drawerOpen}
        zone={selectedZone}
        onClose={() => setDrawerOpen(false)}
        onSaved={() => load(search.offset ?? 0)}
      />
    </Container>
  );
};

export default DeliveryZoneList;